import { useEffect, useRef } from 'react';
import NotificationTemplateServ from '@api/NotificationTemplates/NotificationTemplateServ';
import { resolveTemplateTexts } from '@api/NotificationTemplates/NotificationTemplates';
import { Order, OrderStatus } from '@api/Orders/Order';
import NotificationServ from '@api/Notifications/NotificationServ';
import { NotificationType } from '@api/Notifications/Notification';
import { LATE_ALERT_MINUTES, getBoardClock, getOrderShortNumber } from '@utils/OrdersBoard';

/**
 * Avisa cuando un pedido se pasa de su tiempo.
 *
 * Vive en el cliente y no en un cron porque **no hay cron**: el server escala a
 * cero y solo despierta con tráfico. El tablero de cocina, en cambio, está
 * abierto todo el día en cada sucursal, así que es quien puede darse cuenta.
 *
 * ⚠️ **Dos capas de protección contra el gasto y los duplicados**, y las dos
 * hacen falta:
 * ① un registro LOCAL de lo ya avisado en esta pestaña — sin él, cada vuelta del
 *   intervalo consultaría Firestore por cada pedido retrasado, y a un chequeo
 *   por minuto eso son miles de lecturas al día por tablet (cada lectura se
 *   paga);
 * ② la idempotencia por `referenceId` en el propio servicio, que es la que evita
 *   que TRES tablets de la misma sucursal creen tres avisos del mismo pedido.
 *
 * El aviso se manda UNA vez por pedido: es una alarma, no un recordatorio que
 * insiste.
 */

/** Cada cuánto se revisa. Un minuto: la alarma es de 15, no hace falta más fino. */
const INTERVALO_MS = 60 * 1000;

/** Los estatus en los que un pedido todavía puede llegar tarde. */
const EN_CURSO: OrderStatus[] = [OrderStatus.Paid, OrderStatus.Preparing];

export const useDelayedOrdersNotifier = (orders: Order[], enabled: boolean = true) => {
    const yaAvisados = useRef<Set<string>>(new Set());
    // Las órdenes cambian en cada snapshot; el intervalo tiene que ver las
    // últimas sin reiniciarse en cada cambio.
    const ordersRef = useRef<Order[]>(orders);
    ordersRef.current = orders;

    useEffect(() => {
        if (!enabled) return undefined;

        const revisar = async () => {
            const ahora = new Date();
            // Primero se decide si hay algo que avisar; las plantillas se leen
            // SOLO entonces. Antes se leían en cada vuelta del intervalo — cada
            // minuto, por tablet, todo el día — aunque no hubiera ningún retraso,
            // y una query sin resultados también cuesta una lectura.
            const retrasados = ordersRef.current
                .filter((order) => order.id && EN_CURSO.includes(order.status))
                .filter((order) => !yaAvisados.current.has(order.id as string))
                .filter((order) => getBoardClock(order, ahora).isLate);
            if (retrasados.length === 0) return;

            const overrides = await NotificationTemplateServ.getOverrides();
            retrasados.forEach((order) => {
                // Se marca ANTES de la escritura: si la red falla, es
                // preferible perder un aviso a mandarlo en bucle.
                yaAvisados.current.add(order.id as string);
                // `void`: `notify` nunca lanza y nadie espera el aviso — la alarma
                // del tablero no puede depender de que Firestore conteste.
                void NotificationServ.notify({
                    // Texto editable desde el admin (staff_delayed_order).
                    ...resolveTemplateTexts(
                        'staff_delayed_order',
                        { folio: getOrderShortNumber(order), minutos: LATE_ALERT_MINUTES },
                        overrides
                    ),
                    type: NotificationType.Order,
                    audience: { branchOfficeIds: [order.branchOfficeId] },
                    link: `/orders?orderId=${order.id}`,
                    referenceId: `pedido-retrasado-${order.id}`
                });
            });
        };

        void revisar();
        const timer = setInterval(() => void revisar(), INTERVALO_MS);
        return () => clearInterval(timer);
    }, [enabled]);
};

export default useDelayedOrdersNotifier;
